import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet, apiPatch, apiPost, apiDelete } from "./client";
import { platformSchema, tenantStatusSchema } from "./schemas";

/**
 * Super yonetici uclari — sozlesme §5: /api/v2/tenants*, /api/v2/admin/overview,
 * /api/v2/tenants/{id}/users*. Semalar backend/app/core/api/tenants.py ile
 * BIREBIR (gorev talimati madde: "gevsek semalar — sikilastir"):
 *  - GET /tenants → {tenants:[...]}; POST/GET/PATCH /tenants/{id} → cıplak tenant
 *  - GET /tenants/{id}/users → {users:[...]}; POST → {user_id,email,created,password}
 *  - PATCH/DELETE /tenants/{id}/users/{uid} → {ok}
 *  - GET /admin/overview → {tenants:[ozet satiri], worker} — TAM Tenant DEGIL
 *    (yalnız id/slug/name/platform/status/last_success_at/last_error/
 *    job_failed_24h/ai_cost_month tasir; store_url/timezone/currency/
 *    data_start_date/sync_mode YOK) — bu yuzden ayri bir sema.
 */

export const tenantSchema = z
  .object({
    id: z.number(),
    slug: z.string(),
    name: z.string(),
    platform: platformSchema,
    store_url: z.string().nullable(),
    status: tenantStatusSchema,
    timezone: z.string(),
    currency: z.string(),
    data_start_date: z.string().nullable(),
    // Faz 7: `sync_mode` (mirror/direct) arayuzde ARTIK GOSTERILMIYOR/
    // DEGISTIRILMIYOR — backend gecis suresince halen donebilir, bu yuzden
    // alan burada OPSIYONEL tutulur (kaldirilmadi, sadece kullanilmiyor).
    sync_mode: z.string().optional(),
  })
  .passthrough();
export type TenantOut = z.infer<typeof tenantSchema>;

export const tenantsResponseSchema = z.object({ tenants: z.array(tenantSchema) });

export const adminOverviewTenantSchema = z
  .object({
    id: z.number(),
    slug: z.string(),
    name: z.string(),
    platform: platformSchema,
    status: tenantStatusSchema,
    last_success_at: z.string().nullable(),
    last_error: z.string().nullable(),
    job_failed_24h: z.number(),
    ai_cost_month: z.number(),
  })
  .passthrough();
export type AdminOverviewTenant = z.infer<typeof adminOverviewTenantSchema>;

export const workerStatusSchema = z.object({
  last_seen: z.string().nullable(),
  alive: z.boolean(),
});
export type WorkerStatus = z.infer<typeof workerStatusSchema>;

export const adminOverviewResponseSchema = z.object({
  tenants: z.array(adminOverviewTenantSchema),
  worker: workerStatusSchema,
});
export type AdminOverview = z.infer<typeof adminOverviewResponseSchema>;

export const tenantUserSchema = z
  .object({
    user_id: z.number(),
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    is_super: z.boolean(),
    role: z.string(),
    status: z.string(),
  })
  .passthrough();
export type TenantUserOut = z.infer<typeof tenantUserSchema>;

export const tenantUsersResponseSchema = z.object({ users: z.array(tenantUserSchema) });

/** POST /tenants/{id}/users — {ok} degil, yeni kullaniciysa `password` bir
 * KEZ doner (mevcut kullaniciysa/kendi sifresi verildiyse null). */
export const addTenantUserResponseSchema = z.object({
  user_id: z.number(),
  email: z.string(),
  created: z.boolean(),
  password: z.string().nullable(),
});
export type AddTenantUserResponse = z.infer<typeof addTenantUserResponseSchema>;

export const okResponseSchema = z.object({ ok: z.boolean() });

/** POST /tenant/users/{id}/password — gorev C: mevcut uye icin sifre
 * sifirlama (eski legacy/admin/users.html 🔑 modali). `generated_password`
 * yalniz govdede `password` VERILMEDIYSE dolu doner (backend rastgele uretir). */
export const setTenantUserPasswordResponseSchema = z.object({
  user_id: z.number(),
  generated_password: z.string().nullable(),
});
export type SetTenantUserPasswordResponse = z.infer<typeof setTenantUserPasswordResponseSchema>;

export interface SetTenantUserPasswordPayload {
  password?: string;
}

export interface CreateTenantPayload {
  slug: string;
  name: string;
  platform: "ticimax" | "ikas" | "ideasoft" | "shopify";
  store_url?: string;
  data_start_date?: string;
}

export interface AddTenantUserPayload {
  email: string;
  first_name?: string;
  last_name?: string;
  role?: "tenant_admin" | "tenant_user";
  password?: string;
}

export interface UpdateTenantUserPayload {
  role?: "tenant_admin" | "tenant_user";
  status?: "pending" | "active" | "rejected";
  rejection_reason?: string;
}

export function fetchTenants(includeArchived = false): Promise<TenantOut[]> {
  return apiGet(`/api/v2/tenants?include_archived=${includeArchived}`, tenantsResponseSchema).then(
    (res) => res.tenants,
  );
}

export function createTenantRequest(payload: CreateTenantPayload) {
  return apiPost("/api/v2/tenants", tenantSchema, payload);
}

export function fetchTenant(tenantId: number) {
  return apiGet(`/api/v2/tenants/${tenantId}`, tenantSchema);
}

export function updateTenantRequest(tenantId: number, payload: Record<string, unknown>) {
  return apiPatch(`/api/v2/tenants/${tenantId}`, tenantSchema, payload);
}

export function fetchAdminOverview(): Promise<AdminOverview> {
  return apiGet("/api/v2/admin/overview", adminOverviewResponseSchema);
}

export function fetchTenantUsers(tenantId: number, statusFilter?: string): Promise<TenantUserOut[]> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
  return apiGet(`/api/v2/tenants/${tenantId}/users${qs}`, tenantUsersResponseSchema).then((res) => res.users);
}

export function addTenantUserRequest(
  tenantId: number,
  payload: AddTenantUserPayload,
): Promise<AddTenantUserResponse> {
  return apiPost(`/api/v2/tenants/${tenantId}/users`, addTenantUserResponseSchema, payload);
}

export function updateTenantUserRequest(tenantId: number, userId: number, payload: UpdateTenantUserPayload) {
  return apiPatch(`/api/v2/tenants/${tenantId}/users/${userId}`, okResponseSchema, payload);
}

export function removeTenantUserRequest(tenantId: number, userId: number) {
  return apiDelete(`/api/v2/tenants/${tenantId}/users/${userId}`, okResponseSchema);
}

/** Gorev B (esitlik) — eski legacy/js/auth.js injectAdminTabs "Bekleyen
 * Onaylar" rozeti. AKTIF (oturumdaki) tenant uzerinden calisir — `/tenant/
 * users/pending-count` (tenant_admin/super, backend/app/core/api/tenants.py). */
export const pendingUserCountSchema = z.object({ count: z.number() });
export type PendingUserCount = z.infer<typeof pendingUserCountSchema>;

export function fetchPendingUserCount(): Promise<PendingUserCount> {
  return apiGet("/api/v2/tenant/users/pending-count", pendingUserCountSchema);
}

/** `/tenants/{tenantId}/users/*`'in aksine AKTIF (oturumdaki) tenant
 * uzerinden calisir — backend `/tenant/users/{user_id}/password` (tekil
 * "tenant", `/api/v2/tenant` ile ayni desen), URL'de tenantId TASIMAZ. */
export function setTenantUserPasswordRequest(
  userId: number,
  payload: SetTenantUserPasswordPayload,
): Promise<SetTenantUserPasswordResponse> {
  return apiPost(`/api/v2/tenant/users/${userId}/password`, setTenantUserPasswordResponseSchema, payload);
}

// ---------------------------------------------------------------------------
// Kancalar
// ---------------------------------------------------------------------------

export const ADMIN_TENANTS_QUERY_KEY = ["admin", "tenants"] as const;
export const ADMIN_OVERVIEW_QUERY_KEY = ["admin", "overview"] as const;

export function useTenantsQuery() {
  return useQuery({ queryKey: ADMIN_TENANTS_QUERY_KEY, queryFn: () => fetchTenants() });
}

export function useCreateTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTenantRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TENANTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_OVERVIEW_QUERY_KEY });
    },
  });
}

export function useUpdateTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, payload }: { tenantId: number; payload: Record<string, unknown> }) =>
      updateTenantRequest(tenantId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TENANTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_OVERVIEW_QUERY_KEY });
    },
  });
}

export function useAdminOverviewQuery() {
  return useQuery({ queryKey: ADMIN_OVERVIEW_QUERY_KEY, queryFn: fetchAdminOverview });
}

export function tenantUsersQueryKey(tenantId: number) {
  return ["tenant", tenantId, "users"] as const;
}

export function useTenantUsersQuery(tenantId: number, statusFilter?: string) {
  return useQuery({
    queryKey: [...tenantUsersQueryKey(tenantId), statusFilter ?? "all"],
    queryFn: () => fetchTenantUsers(tenantId, statusFilter),
    enabled: Number.isFinite(tenantId),
  });
}

export function useAddTenantUserMutation(tenantId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddTenantUserPayload) => addTenantUserRequest(tenantId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tenantUsersQueryKey(tenantId) }),
  });
}

export function useUpdateTenantUserMutation(tenantId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: UpdateTenantUserPayload }) =>
      updateTenantUserRequest(tenantId, userId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tenantUsersQueryKey(tenantId) }),
  });
}

export function useRemoveTenantUserMutation(tenantId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => removeTenantUserRequest(tenantId, userId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tenantUsersQueryKey(tenantId) }),
  });
}

/** Sifre listede gorunmez — basarili olsa da uye listesini invalidate ETMEYE
 * gerek yok (bkz. removeTenantUserRequest'in aksine `onSuccess` yok). */
export function useSetTenantUserPasswordMutation() {
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: SetTenantUserPasswordPayload }) =>
      setTenantUserPasswordRequest(userId, payload),
  });
}

export const PENDING_USER_COUNT_QUERY_KEY = ["tenant", "users", "pending-count"] as const;

/** Gorev B (esitlik) — nav rozeti icin 5 dk'da bir yenilenir (legacy her sayfa
 * yuklemesinde requireLogin() ile tazeliyordu; SPA'da periyodik yenileme ile
 * esdeger). `enabled` cagiran tarafta (useVisibleTabs) isTenantAdmin ile kisitlanir. */
export function usePendingUserCountQuery(enabled: boolean) {
  return useQuery({
    queryKey: PENDING_USER_COUNT_QUERY_KEY,
    queryFn: fetchPendingUserCount,
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}
